/**
 * Slingu — Pool unificado de vocabulário PT → EN.
 *
 * ESTE É O CONTRATO ÚNICO pra todos os minigames (Flappy, CarRacer, Quiz*, Word*).
 * Não há mais seleção de tema — o jogador recebe palavras do pool inteiro,
 * embaralhado dentro de blocos de dificuldade pra preservar a curva A1 → B2.
 *
 * Schema:
 *   - `pt`      — prompt em português (o que mostrar ao jogador)
 *   - `en`      — tradução correta em inglês (resposta certa)
 *   - `wrong`   — distrator plausível (cognato falso, rima, grafia próxima)
 *   - `level`   — CEFR A1 | A2 | B1 | B2 — ordena dificuldade dentro do bloco
 *   - `topic`   — tópico interno (animais, comida, etc.) — usado apenas para
 *                 autoria/admin, NUNCA exposto ao jogador
 *
 * Mirror em banco: `packages/db/src/schema/vocabulary.ts` (tabela `vocab_pairs`).
 * Seed: `packages/db/src/seed-vocabulary.ts` lê este arquivo como fonte de
 * verdade e popula o banco de produção.
 */

export type VocabLevel = 'A1' | 'A2' | 'B1' | 'B2';

export type VocabTopic =
  | 'animals'
  | 'food'
  | 'colors-body'
  | 'house'
  | 'nature'
  | 'verbs'
  | 'work-school'
  | 'transport-city'
  | 'time-numbers'
  | 'emotions-social';

export type VocabPair = {
  pt: string;
  en: string;
  wrong: string;
  level: VocabLevel;
  topic: VocabTopic;
};

// Backward-compat pra quem ainda usa FlappyPair.
export type FlappyPair = {
  correct: string;
  wrong: string;
  promptPt?: string;
};

const p = (
  pt: string,
  en: string,
  wrong: string,
  level: VocabLevel,
  topic: VocabTopic,
): VocabPair => ({ pt, en, wrong, level, topic });

// ========================================================================
// BLOCO 1 — 🐾 Animals (100 pares)
// ========================================================================
const animals: VocabPair[] = [
  p('gato', 'cat', 'cap', 'A1', 'animals'),
  p('cachorro', 'dog', 'dot', 'A1', 'animals'),
  p('peixe', 'fish', 'dish', 'A1', 'animals'),
  p('pássaro', 'bird', 'beard', 'A1', 'animals'),
  p('vaca', 'cow', 'cod', 'A1', 'animals'),
  p('cavalo', 'horse', 'house', 'A1', 'animals'),
  p('porco', 'pig', 'big', 'A1', 'animals'),
  p('ovelha', 'sheep', 'ship', 'A1', 'animals'),
  p('pato', 'duck', 'dock', 'A1', 'animals'),
  p('galinha', 'hen', 'hex', 'A1', 'animals'),
  p('galo', 'rooster', 'roaster', 'A1', 'animals'),
  p('coelho', 'rabbit', 'robot', 'A1', 'animals'),
  p('rato', 'mouse', 'house', 'A1', 'animals'),
  p('abelha', 'bee', 'bed', 'A1', 'animals'),
  p('mosca', 'fly', 'fry', 'A1', 'animals'),
  p('formiga', 'ant', 'art', 'A1', 'animals'),
  p('aranha', 'spider', 'spinner', 'A1', 'animals'),
  p('urso', 'bear', 'beer', 'A1', 'animals'),
  p('leão', 'lion', 'line', 'A1', 'animals'),
  p('tigre', 'tiger', 'tinker', 'A1', 'animals'),
  p('macaco', 'monkey', 'money', 'A1', 'animals'),
  p('elefante', 'elephant', 'elegant', 'A1', 'animals'),
  p('cobra', 'snake', 'snack', 'A1', 'animals'),
  p('tubarão', 'shark', 'shock', 'A1', 'animals'),
  p('baleia', 'whale', 'wheel', 'A1', 'animals'),
  p('polvo', 'octopus', 'octagon', 'A1', 'animals'),
  p('golfinho', 'dolphin', 'daffodil', 'A1', 'animals'),
  p('coruja', 'owl', 'oil', 'A1', 'animals'),
  p('pomba', 'dove', 'drove', 'A1', 'animals'),
  p('tartaruga', 'turtle', 'tunnel', 'A1', 'animals'),
  p('sapo', 'frog', 'flag', 'A2', 'animals'),
  p('lagarto', 'lizard', 'wizard', 'A2', 'animals'),
  p('morcego', 'bat', 'bad', 'A2', 'animals'),
  p('esquilo', 'squirrel', 'squeal', 'A2', 'animals'),
  p('veado', 'deer', 'dare', 'A2', 'animals'),
  p('raposa', 'fox', 'fix', 'A2', 'animals'),
  p('lobo', 'wolf', 'wool', 'A2', 'animals'),
  p('hipopótamo', 'hippo', 'hippie', 'A2', 'animals'),
  p('rinoceronte', 'rhino', 'ruin', 'A2', 'animals'),
  p('canguru', 'kangaroo', 'cockatoo', 'A2', 'animals'),
  p('coala', 'koala', 'kola', 'A2', 'animals'),
  p('panda', 'panda', 'pander', 'A2', 'animals'),
  p('zebra', 'zebra', 'zero', 'A2', 'animals'),
  p('camelo', 'camel', 'caramel', 'A2', 'animals'),
  p('avestruz', 'ostrich', 'orchid', 'A2', 'animals'),
  p('águia', 'eagle', 'eager', 'A2', 'animals'),
  p('pavão', 'peacock', 'paycheck', 'A2', 'animals'),
  p('papagaio', 'parrot', 'carrot', 'A2', 'animals'),
  p('pinguim', 'penguin', 'pending', 'A2', 'animals'),
  p('cisne', 'swan', 'scan', 'A2', 'animals'),
  p('borboleta', 'butterfly', 'firefly', 'A2', 'animals'),
  p('besouro', 'beetle', 'bottle', 'A2', 'animals'),
  p('caracol', 'snail', 'snake', 'A2', 'animals'),
  p('minhoca', 'worm', 'warm', 'A2', 'animals'),
  p('crocodilo', 'crocodile', 'chronicle', 'A2', 'animals'),
  p('girafa', 'giraffe', 'graph', 'A2', 'animals'),
  p('foca', 'seal', 'sail', 'A2', 'animals'),
  p('lontra', 'otter', 'utter', 'A2', 'animals'),
  p('castor', 'beaver', 'bearer', 'A2', 'animals'),
  p('guaxinim', 'raccoon', 'balloon', 'A2', 'animals'),
  p('gambá', 'skunk', 'chunk', 'A2', 'animals'),
  p('porco-espinho', 'porcupine', 'porcelain', 'B1', 'animals'),
  p('ouriço', 'hedgehog', 'hedonism', 'B1', 'animals'),
  p('toupeira', 'mole', 'mild', 'B1', 'animals'),
  p('cavalo-marinho', 'seahorse', 'seacourse', 'B1', 'animals'),
  p('caranguejo', 'crab', 'crib', 'B1', 'animals'),
  p('lagosta', 'lobster', 'booster', 'B1', 'animals'),
  p('camarão', 'shrimp', 'stamp', 'B1', 'animals'),
  p('estrela-do-mar', 'starfish', 'starship', 'B1', 'animals'),
  p('medusa', 'jellyfish', 'jellybean', 'B1', 'animals'),
  p('camaleão', 'chameleon', 'chamomile', 'B1', 'animals'),
  p('lagartixa', 'gecko', 'ghetto', 'B1', 'animals'),
  p('iguana', 'iguana', 'iguanid', 'B1', 'animals'),
  p('serpente', 'serpent', 'servant', 'B1', 'animals'),
  p('víbora', 'viper', 'wiper', 'B1', 'animals'),
  p('tarântula', 'tarantula', 'tabernacle', 'B1', 'animals'),
  p('escorpião', 'scorpion', 'sojourn', 'B1', 'animals'),
  p('centopeia', 'centipede', 'centigrade', 'B1', 'animals'),
  p('libélula', 'dragonfly', 'firefly', 'B1', 'animals'),
  p('grilo', 'cricket', 'bracket', 'B1', 'animals'),
  p('mariposa', 'moth', 'math', 'B1', 'animals'),
  p('pulga', 'flea', 'flee', 'B1', 'animals'),
  p('carrapato', 'tick', 'teak', 'B2', 'animals'),
  p('peru', 'turkey', 'torque', 'B2', 'animals'),
  p('gaivota', 'seagull', 'segue', 'B2', 'animals'),
  p('falcão', 'falcon', 'falcate', 'B2', 'animals'),
  p('gavião', 'hawk', 'hack', 'B2', 'animals'),
  p('corvo', 'crow', 'crew', 'B2', 'animals'),
  p('bisão', 'bison', 'basin', 'B2', 'animals'),
  p('javali', 'boar', 'bore', 'B2', 'animals'),
  p('carneiro', 'ram', 'rim', 'B2', 'animals'),
  p('cabra', 'goat', 'goad', 'B2', 'animals'),
  p('bezerro', 'calf', 'calm', 'B2', 'animals'),
  p('potro', 'foal', 'foul', 'B2', 'animals'),
  p('lebre', 'hare', 'heir', 'B2', 'animals'),
  p('furão', 'ferret', 'forest', 'B2', 'animals'),
  p('doninha', 'weasel', 'wheezy', 'B2', 'animals'),
  p('preguiça', 'sloth', 'slot', 'B2', 'animals'),
  p('morsa', 'walrus', 'walnut', 'B2', 'animals'),
  p('peixe-palhaço', 'clownfish', 'crownfish', 'B2', 'animals'),
  p('arara', 'macaw', 'mock', 'B2', 'animals'),
  p('tucano', 'toucan', 'two-can', 'B2', 'animals'),
];

// ========================================================================
// BLOCO 2 — 🍎 Food & Drink (100 pares)
// ========================================================================
const food: VocabPair[] = [
  p('pão', 'bread', 'beard', 'A1', 'food'),
  p('água', 'water', 'matter', 'A1', 'food'),
  p('leite', 'milk', 'silk', 'A1', 'food'),
  p('ovo', 'egg', 'edge', 'A1', 'food'),
  p('arroz', 'rice', 'race', 'A1', 'food'),
  p('feijão', 'beans', 'bears', 'A1', 'food'),
  p('queijo', 'cheese', 'chess', 'A1', 'food'),
  p('manteiga', 'butter', 'batter', 'A1', 'food'),
  p('açúcar', 'sugar', 'seguer', 'A1', 'food'),
  p('sal', 'salt', 'silt', 'A1', 'food'),
  p('pimenta', 'pepper', 'paper', 'A1', 'food'),
  p('café', 'coffee', 'coffin', 'A1', 'food'),
  p('chá', 'tea', 'toe', 'A1', 'food'),
  p('suco', 'juice', 'juicer', 'A1', 'food'),
  p('vinho', 'wine', 'vine', 'A1', 'food'),
  p('cerveja', 'beer', 'bear', 'A1', 'food'),
  p('maçã', 'apple', 'ample', 'A1', 'food'),
  p('banana', 'banana', 'bandana', 'A1', 'food'),
  p('laranja', 'orange', 'orient', 'A1', 'food'),
  p('uva', 'grape', 'gripe', 'A1', 'food'),
  p('morango', 'strawberry', 'blueberry', 'A1', 'food'),
  p('melancia', 'watermelon', 'watermark', 'A1', 'food'),
  p('abacaxi', 'pineapple', 'pinelane', 'A1', 'food'),
  p('pêra', 'pear', 'peer', 'A1', 'food'),
  p('pêssego', 'peach', 'pitch', 'A1', 'food'),
  p('cereja', 'cherry', 'cherish', 'A1', 'food'),
  p('limão', 'lemon', 'lemur', 'A1', 'food'),
  p('coco', 'coconut', 'coconet', 'A1', 'food'),
  p('manga', 'mango', 'tango', 'A1', 'food'),
  p('tomate', 'tomato', 'tornado', 'A1', 'food'),
  p('cenoura', 'carrot', 'parrot', 'A2', 'food'),
  p('batata', 'potato', 'potter', 'A2', 'food'),
  p('cebola', 'onion', 'union', 'A2', 'food'),
  p('alho', 'garlic', 'garish', 'A2', 'food'),
  p('alface', 'lettuce', 'latter', 'A2', 'food'),
  p('pepino', 'cucumber', 'cumber', 'A2', 'food'),
  p('abóbora', 'pumpkin', 'pumpking', 'A2', 'food'),
  p('milho', 'corn', 'coin', 'A2', 'food'),
  p('beringela', 'eggplant', 'ebbplant', 'A2', 'food'),
  p('brócolis', 'broccoli', 'braccino', 'A2', 'food'),
  p('espinafre', 'spinach', 'spinch', 'A2', 'food'),
  p('couve-flor', 'cauliflower', 'cauliform', 'A2', 'food'),
  p('cogumelo', 'mushroom', 'mudroom', 'A2', 'food'),
  p('pimentão', 'bell-pepper', 'hell-pepper', 'A2', 'food'),
  p('carne', 'meat', 'meet', 'A2', 'food'),
  p('frango', 'chicken', 'kitchen', 'A2', 'food'),
  p('carne-bovina', 'beef', 'beefy', 'A2', 'food'),
  p('presunto', 'ham', 'hum', 'A2', 'food'),
  p('bacon', 'bacon', 'beacon', 'A2', 'food'),
  p('salsicha', 'sausage', 'savage', 'A2', 'food'),
  p('hambúrguer', 'hamburger', 'humbler', 'A2', 'food'),
  p('bife', 'steak', 'stake', 'A2', 'food'),
  p('almôndega', 'meatball', 'meantball', 'A2', 'food'),
  p('atum', 'tuna', 'tunnel', 'A2', 'food'),
  p('salmão', 'salmon', 'sermon', 'A2', 'food'),
  p('bolo', 'cake', 'cage', 'A2', 'food'),
  p('biscoito', 'cookie', 'rookie', 'A2', 'food'),
  p('sorvete', 'ice-cream', 'ice-green', 'A2', 'food'),
  p('chocolate', 'chocolate', 'colleague', 'A2', 'food'),
  p('bala', 'candy', 'canny', 'A2', 'food'),
  p('mel', 'honey', 'money', 'A2', 'food'),
  p('geléia', 'jam', 'gym', 'A2', 'food'),
  p('sopa', 'soup', 'soap', 'A2', 'food'),
  p('salada', 'salad', 'solid', 'A2', 'food'),
  p('pizza', 'pizza', 'pieza', 'A2', 'food'),
  p('sanduíche', 'sandwich', 'sandwidge', 'A2', 'food'),
  p('macarrão', 'pasta', 'paste', 'A2', 'food'),
  p('torta', 'pie', 'pile', 'A2', 'food'),
  p('panqueca', 'pancake', 'pencake', 'A2', 'food'),
  p('rosquinha', 'donut', 'donate', 'A2', 'food'),
  p('torrada', 'toast', 'toasty', 'A2', 'food'),
  p('iogurte', 'yogurt', 'yore-gut', 'A2', 'food'),
  p('refrigerante', 'soda', 'sofa', 'A2', 'food'),
  p('canela', 'cinnamon', 'synonym', 'B1', 'food'),
  p('baunilha', 'vanilla', 'vanity', 'B1', 'food'),
  p('hortelã', 'mint', 'mine', 'B1', 'food'),
  p('salsa', 'parsley', 'parslee', 'B1', 'food'),
  p('coentro', 'cilantro', 'sombrero', 'B1', 'food'),
  p('manjericão', 'basil', 'basic', 'B1', 'food'),
  p('alecrim', 'rosemary', 'ordinary', 'B1', 'food'),
  p('orégano', 'oregano', 'orangutan', 'B1', 'food'),
  p('gengibre', 'ginger', 'gimmick', 'B1', 'food'),
  p('cravo-da-índia', 'clove', 'clovis', 'B1', 'food'),
  p('noz', 'walnut', 'wombat', 'B1', 'food'),
  p('amêndoa', 'almond', 'almighty', 'B1', 'food'),
  p('castanha', 'chestnut', 'cashier', 'B1', 'food'),
  p('amendoim', 'peanut', 'penny', 'B1', 'food'),
  p('avelã', 'hazelnut', 'hazel-not', 'B1', 'food'),
  p('azeite', 'olive-oil', 'olive-owl', 'B1', 'food'),
  p('vinagre', 'vinegar', 'vintage', 'B1', 'food'),
  p('farinha', 'flour', 'flower', 'B1', 'food'),
  p('massa', 'dough', 'bough', 'B1', 'food'),
  p('creme', 'cream', 'creed', 'B1', 'food'),
  p('conhaque', 'cognac', 'coiner', 'B2', 'food'),
  p('champanhe', 'champagne', 'champion', 'B2', 'food'),
  p('uísque', 'whiskey', 'wispy', 'B2', 'food'),
  p('tempero', 'seasoning', 'seasonal', 'B2', 'food'),
  p('molho', 'sauce', 'source', 'B2', 'food'),
  p('mostarda', 'mustard', 'muster', 'B2', 'food'),
  p('maionese', 'mayo', 'maya', 'B2', 'food'),
  p('maracujá', 'passionfruit', 'pastefruit', 'B2', 'food'),
  p('goiaba', 'guava', 'gala', 'B2', 'food'),
];

// ========================================================================
// BLOCO 3 — 🎨 Colors & Body (100 pares)
// ========================================================================
const colorsBody: VocabPair[] = [
  p('vermelho', 'red', 'read', 'A1', 'colors-body'),
  p('azul', 'blue', 'blew', 'A1', 'colors-body'),
  p('amarelo', 'yellow', 'fellow', 'A1', 'colors-body'),
  p('verde', 'green', 'grin', 'A1', 'colors-body'),
  p('preto', 'black', 'block', 'A1', 'colors-body'),
  p('branco', 'white', 'whale', 'A1', 'colors-body'),
  p('rosa', 'pink', 'punk', 'A1', 'colors-body'),
  p('roxo', 'purple', 'people', 'A1', 'colors-body'),
  p('marrom', 'brown', 'brain', 'A1', 'colors-body'),
  p('cinza', 'grey', 'greyhound', 'A1', 'colors-body'),
  p('dourado', 'gold', 'gilt', 'A1', 'colors-body'),
  p('prateado', 'silver', 'sliver', 'A1', 'colors-body'),
  p('bege', 'beige', 'binge', 'A1', 'colors-body'),
  p('turquesa', 'turquoise', 'turkese', 'A1', 'colors-body'),
  p('azul-marinho', 'navy', 'nave', 'A1', 'colors-body'),
  p('vinho', 'maroon', 'moon', 'A1', 'colors-body'),
  p('lilás', 'lilac', 'lilah', 'A1', 'colors-body'),
  p('coral', 'coral', 'corral', 'A1', 'colors-body'),
  p('cabeça', 'head', 'heart', 'A1', 'colors-body'),
  p('cabelo', 'hair', 'hare', 'A1', 'colors-body'),
  p('olho', 'eye', 'ear', 'A1', 'colors-body'),
  p('nariz', 'nose', 'noise', 'A1', 'colors-body'),
  p('boca', 'mouth', 'month', 'A1', 'colors-body'),
  p('orelha', 'ear', 'ark', 'A1', 'colors-body'),
  p('dente', 'tooth', 'booth', 'A1', 'colors-body'),
  p('língua', 'tongue', 'tangle', 'A1', 'colors-body'),
  p('lábio', 'lip', 'lap', 'A1', 'colors-body'),
  p('queixo', 'chin', 'coin', 'A1', 'colors-body'),
  p('testa', 'forehead', 'forward', 'A1', 'colors-body'),
  p('bochecha', 'cheek', 'cheat', 'A1', 'colors-body'),
  p('sobrancelha', 'eyebrow', 'elbow', 'A2', 'colors-body'),
  p('cílios', 'eyelashes', 'eye-ashes', 'A2', 'colors-body'),
  p('pescoço', 'neck', 'nick', 'A2', 'colors-body'),
  p('ombro', 'shoulder', 'solder', 'A2', 'colors-body'),
  p('braço', 'arm', 'aim', 'A2', 'colors-body'),
  p('cotovelo', 'elbow', 'below', 'A2', 'colors-body'),
  p('pulso', 'wrist', 'wrest', 'A2', 'colors-body'),
  p('mão', 'hand', 'hound', 'A2', 'colors-body'),
  p('dedo', 'finger', 'fringe', 'A2', 'colors-body'),
  p('polegar', 'thumb', 'tomb', 'A2', 'colors-body'),
  p('unha', 'nail', 'nailer', 'A2', 'colors-body'),
  p('palma', 'palm', 'calm', 'A2', 'colors-body'),
  p('costas', 'back', 'bark', 'A2', 'colors-body'),
  p('peito', 'chest', 'guest', 'A2', 'colors-body'),
  p('coração', 'heart', 'earth', 'A2', 'colors-body'),
  p('pulmão', 'lung', 'long', 'A2', 'colors-body'),
  p('estômago', 'stomach', 'stomping', 'A2', 'colors-body'),
  p('barriga', 'belly', 'billy', 'A2', 'colors-body'),
  p('quadril', 'hip', 'hop', 'A2', 'colors-body'),
  p('cintura', 'waist', 'waste', 'A2', 'colors-body'),
  p('perna', 'leg', 'lag', 'A2', 'colors-body'),
  p('joelho', 'knee', 'knew', 'A2', 'colors-body'),
  p('canela', 'shin', 'shine', 'A2', 'colors-body'),
  p('tornozelo', 'ankle', 'uncle', 'A2', 'colors-body'),
  p('pé', 'foot', 'food', 'A2', 'colors-body'),
  p('dedo-do-pé', 'toe', 'tow', 'A2', 'colors-body'),
  p('calcanhar', 'heel', 'heal', 'A2', 'colors-body'),
  p('osso', 'bone', 'bono', 'A2', 'colors-body'),
  p('pele', 'skin', 'sin', 'A2', 'colors-body'),
  p('sangue', 'blood', 'bloody', 'A2', 'colors-body'),
  p('cérebro', 'brain', 'brainy', 'B1', 'colors-body'),
  p('rim', 'kidney', 'kidner', 'B1', 'colors-body'),
  p('fígado', 'liver', 'livery', 'B1', 'colors-body'),
  p('intestino', 'intestine', 'intensive', 'B1', 'colors-body'),
  p('veia', 'vein', 'vane', 'B1', 'colors-body'),
  p('nervo', 'nerve', 'never', 'B1', 'colors-body'),
  p('músculo', 'muscle', 'mussel', 'B1', 'colors-body'),
  p('articulação', 'joint', 'point', 'B1', 'colors-body'),
  p('tendão', 'tendon', 'tension', 'B1', 'colors-body'),
  p('garganta', 'throat', 'thrown', 'B1', 'colors-body'),
  p('axila', 'armpit', 'harmonic', 'B1', 'colors-body'),
  p('rosto', 'face', 'fence', 'B1', 'colors-body'),
  p('bigode', 'mustache', 'misstep', 'B1', 'colors-body'),
  p('barba', 'beard', 'bread', 'B1', 'colors-body'),
  p('crânio', 'skull', 'scull', 'B2', 'colors-body'),
  p('mandíbula', 'jaw', 'jay', 'B2', 'colors-body'),
  p('costela', 'rib', 'rib-on', 'B2', 'colors-body'),
  p('coluna', 'spine', 'spined', 'B2', 'colors-body'),
  p('pâncreas', 'pancreas', 'parquet', 'B2', 'colors-body'),
  p('bexiga', 'bladder', 'blather', 'B2', 'colors-body'),
  p('traquéia', 'trachea', 'tractor', 'B2', 'colors-body'),
  p('artéria', 'artery', 'array', 'B2', 'colors-body'),
  p('tecido', 'tissue', 'issue', 'B2', 'colors-body'),
  p('célula', 'cell', 'sell', 'B2', 'colors-body'),
  p('retina', 'retina', 'return', 'B2', 'colors-body'),
  p('íris', 'iris', 'arise', 'B2', 'colors-body'),
  p('córnea', 'cornea', 'coronet', 'B2', 'colors-body'),
  p('pupila', 'pupil', 'pupal', 'B2', 'colors-body'),
  p('esmalte', 'enamel', 'animal', 'B2', 'colors-body'),
  p('gengiva', 'gum', 'gym', 'B2', 'colors-body'),
  p('sarda', 'freckle', 'speckle', 'B2', 'colors-body'),
  p('ruga', 'wrinkle', 'twinkle', 'B2', 'colors-body'),
  p('diafragma', 'diaphragm', 'diagram', 'B2', 'colors-body'),
  p('tímpano', 'eardrum', 'earthworm', 'B2', 'colors-body'),
  p('carmesim', 'crimson', 'crimpson', 'B2', 'colors-body'),
  p('escarlate', 'scarlet', 'starlet', 'B2', 'colors-body'),
  p('safira', 'sapphire', 'saphyre', 'B2', 'colors-body'),
  p('esmeralda', 'emerald', 'admiral', 'B2', 'colors-body'),
  p('âmbar', 'amber', 'ember', 'B2', 'colors-body'),
  p('marfim', 'ivory', 'ivy', 'B2', 'colors-body'),
  p('ébano', 'ebony', 'bonny', 'B2', 'colors-body'),
  p('cromo', 'chrome', 'crome', 'B2', 'colors-body'),
  p('pigmento', 'pigment', 'figment', 'B2', 'colors-body'),
];

// ========================================================================
// BLOCO 4 — 🏠 House & Objects (100 pares)
// ========================================================================
const house: VocabPair[] = [
  p('casa', 'house', 'horse', 'A1', 'house'),
  p('porta', 'door', 'doer', 'A1', 'house'),
  p('janela', 'window', 'widow', 'A1', 'house'),
  p('parede', 'wall', 'walk', 'A1', 'house'),
  p('teto', 'ceiling', 'sealing', 'A1', 'house'),
  p('chão', 'floor', 'flour', 'A1', 'house'),
  p('cama', 'bed', 'bud', 'A1', 'house'),
  p('mesa', 'table', 'stable', 'A1', 'house'),
  p('cadeira', 'chair', 'cheer', 'A1', 'house'),
  p('sofá', 'sofa', 'soda', 'A1', 'house'),
  p('armário', 'wardrobe', 'wardrog', 'A1', 'house'),
  p('estante', 'shelf', 'self', 'A1', 'house'),
  p('gaveta', 'drawer', 'drover', 'A1', 'house'),
  p('espelho', 'mirror', 'minor', 'A1', 'house'),
  p('tapete', 'rug', 'ruby', 'A1', 'house'),
  p('cortina', 'curtain', 'certain', 'A1', 'house'),
  p('luz', 'light', 'right', 'A1', 'house'),
  p('lâmpada', 'lamp', 'damp', 'A1', 'house'),
  p('vela', 'candle', 'candor', 'A1', 'house'),
  p('chave', 'key', 'kid', 'A1', 'house'),
  p('fechadura', 'lock', 'luck', 'A1', 'house'),
  p('tomada', 'outlet', 'outline', 'A1', 'house'),
  p('telefone', 'phone', 'phony', 'A1', 'house'),
  p('televisão', 'television', 'telephone', 'A1', 'house'),
  p('rádio', 'radio', 'ratio', 'A1', 'house'),
  p('computador', 'computer', 'commuter', 'A1', 'house'),
  p('teclado', 'keyboard', 'keycard', 'A1', 'house'),
  p('tela', 'screen', 'greens', 'A1', 'house'),
  p('geladeira', 'fridge', 'bridge', 'A1', 'house'),
  p('forno', 'oven', 'over', 'A1', 'house'),
  p('fogão', 'stove', 'stole', 'A2', 'house'),
  p('microondas', 'microwave', 'microware', 'A2', 'house'),
  p('liquidificador', 'blender', 'bender', 'A2', 'house'),
  p('batedeira', 'mixer', 'miser', 'A2', 'house'),
  p('torradeira', 'toaster', 'tester', 'A2', 'house'),
  p('panela', 'pot', 'pet', 'A2', 'house'),
  p('frigideira', 'pan', 'pun', 'A2', 'house'),
  p('caçarola', 'saucepan', 'saucy-pan', 'A2', 'house'),
  p('prato', 'plate', 'plant', 'A2', 'house'),
  p('copo', 'glass', 'grass', 'A2', 'house'),
  p('xícara', 'cup', 'cap', 'A2', 'house'),
  p('caneca', 'mug', 'mag', 'A2', 'house'),
  p('garrafa', 'bottle', 'battle', 'A2', 'house'),
  p('faca', 'knife', 'knee', 'A2', 'house'),
  p('garfo', 'fork', 'folk', 'A2', 'house'),
  p('colher', 'spoon', 'soon', 'A2', 'house'),
  p('tigela', 'bowl', 'boil', 'A2', 'house'),
  p('bandeja', 'tray', 'trail', 'A2', 'house'),
  p('guardanapo', 'napkin', 'knapkin', 'A2', 'house'),
  p('toalha', 'towel', 'toweled', 'A2', 'house'),
  p('escova', 'brush', 'brash', 'A2', 'house'),
  p('sabonete', 'soap', 'soup', 'A2', 'house'),
  p('shampoo', 'shampoo', 'shambler', 'A2', 'house'),
  p('pente', 'comb', 'bomb', 'A2', 'house'),
  p('banheira', 'bathtub', 'bathrobe', 'A2', 'house'),
  p('chuveiro', 'shower', 'shoveler', 'A2', 'house'),
  p('vaso-sanitário', 'toilet', 'tollet', 'A2', 'house'),
  p('pia', 'sink', 'silk', 'A2', 'house'),
  p('torneira', 'faucet', 'faces', 'A2', 'house'),
  p('caixa', 'box', 'boo', 'A2', 'house'),
  p('sacola', 'bag', 'bog', 'A2', 'house'),
  p('mochila', 'backpack', 'backtrack', 'B1', 'house'),
  p('carteira', 'wallet', 'willet', 'B1', 'house'),
  p('óculos', 'glasses', 'grasses', 'B1', 'house'),
  p('relógio', 'clock', 'cloak', 'B1', 'house'),
  p('livro', 'book', 'bock', 'B1', 'house'),
  p('caneta', 'pen', 'pan', 'B1', 'house'),
  p('lápis', 'pencil', 'pencils', 'B1', 'house'),
  p('papel', 'paper', 'peppers', 'B1', 'house'),
  p('caderno', 'notebook', 'notetook', 'B1', 'house'),
  p('tesoura', 'scissors', 'scissure', 'B1', 'house'),
  p('cola', 'glue', 'blue', 'B1', 'house'),
  p('régua', 'ruler', 'rulier', 'B1', 'house'),
  p('borracha', 'eraser', 'erasion', 'B1', 'house'),
  p('lixeira', 'trash-can', 'trash-cane', 'B1', 'house'),
  p('vassoura', 'broom', 'boom', 'B1', 'house'),
  p('pá', 'dustpan', 'disspan', 'B1', 'house'),
  p('esfregão', 'mop', 'map', 'B1', 'house'),
  p('aspirador', 'vacuum', 'voom', 'B1', 'house'),
  p('ferro-de-passar', 'iron', 'ivy', 'B1', 'house'),
  p('máquina-de-lavar', 'washer', 'wisher', 'B1', 'house'),
  p('secadora', 'dryer', 'dyer', 'B2', 'house'),
  p('ventilador', 'fan', 'fin', 'B2', 'house'),
  p('aquecedor', 'heater', 'heated', 'B2', 'house'),
  p('cobertor', 'blanket', 'plankett', 'B2', 'house'),
  p('travesseiro', 'pillow', 'pillar', 'B2', 'house'),
  p('lençol', 'sheet', 'sheep', 'B2', 'house'),
  p('colcha', 'quilt', 'guilt', 'B2', 'house'),
  p('edredom', 'duvet', 'divot', 'B2', 'house'),
  p('escada', 'stairs', 'stares', 'B2', 'house'),
  p('elevador', 'elevator', 'alligator', 'B2', 'house'),
  p('telhado', 'roof', 'room', 'B2', 'house'),
  p('chaminé', 'chimney', 'chimnier', 'B2', 'house'),
  p('varanda', 'balcony', 'baloney', 'B2', 'house'),
  p('jardim', 'garden', 'cordon', 'B2', 'house'),
  p('garagem', 'garage', 'garish', 'B2', 'house'),
  p('porão', 'basement', 'basemento', 'B2', 'house'),
  p('sótão', 'attic', 'antic', 'B2', 'house'),
  p('corredor', 'hallway', 'halfway', 'B2', 'house'),
  p('interruptor', 'switch', 'stitch', 'B2', 'house'),
  p('fio', 'wire', 'were', 'B2', 'house'),
  p('carregador', 'charger', 'charter', 'B2', 'house'),
  p('controle-remoto', 'remote', 'remorse', 'B2', 'house'),
];

// ========================================================================
// BLOCO 5 — 🌳 Nature & Weather (100 pares)
// ========================================================================
const nature: VocabPair[] = [
  p('sol', 'sun', 'son', 'A1', 'nature'),
  p('lua', 'moon', 'moan', 'A1', 'nature'),
  p('estrela', 'star', 'stare', 'A1', 'nature'),
  p('céu', 'sky', 'ski', 'A1', 'nature'),
  p('nuvem', 'cloud', 'clout', 'A1', 'nature'),
  p('chuva', 'rain', 'reign', 'A1', 'nature'),
  p('neve', 'snow', 'slow', 'A1', 'nature'),
  p('vento', 'wind', 'wound', 'A1', 'nature'),
  p('trovão', 'thunder', 'thundery', 'A1', 'nature'),
  p('raio', 'lightning', 'lightening', 'A1', 'nature'),
  p('arco-íris', 'rainbow', 'rainbox', 'A1', 'nature'),
  p('névoa', 'fog', 'frog', 'A1', 'nature'),
  p('gelo', 'ice', 'icy', 'A1', 'nature'),
  p('fogo', 'fire', 'fair', 'A1', 'nature'),
  p('terra', 'earth', 'ear', 'A1', 'nature'),
  p('ar', 'air', 'airy', 'A1', 'nature'),
  p('árvore', 'tree', 'three', 'A1', 'nature'),
  p('flor', 'flower', 'flourish', 'A1', 'nature'),
  p('folha', 'leaf', 'loaf', 'A1', 'nature'),
  p('grama', 'grass', 'glass', 'A1', 'nature'),
  p('raiz', 'root', 'rout', 'A1', 'nature'),
  p('galho', 'branch', 'brunch', 'A1', 'nature'),
  p('mato', 'bush', 'bash', 'A1', 'nature'),
  p('planta', 'plant', 'plane', 'A1', 'nature'),
  p('semente', 'seed', 'seat', 'A1', 'nature'),
  p('fruta', 'fruit', 'fruity', 'A1', 'nature'),
  p('pedra', 'stone', 'stove', 'A1', 'nature'),
  p('rocha', 'rock', 'rook', 'A1', 'nature'),
  p('areia', 'sand', 'sane', 'A1', 'nature'),
  p('rio', 'river', 'diver', 'A1', 'nature'),
  p('lago', 'lake', 'lace', 'A2', 'nature'),
  p('mar', 'sea', 'see', 'A2', 'nature'),
  p('oceano', 'ocean', 'open', 'A2', 'nature'),
  p('onda', 'wave', 'wade', 'A2', 'nature'),
  p('praia', 'beach', 'peach', 'A2', 'nature'),
  p('ilha', 'island', 'ireland', 'A2', 'nature'),
  p('montanha', 'mountain', 'fountain', 'A2', 'nature'),
  p('vale', 'valley', 'alley', 'A2', 'nature'),
  p('colina', 'hill', 'hell', 'A2', 'nature'),
  p('deserto', 'desert', 'dessert', 'A2', 'nature'),
  p('floresta', 'forest', 'florist', 'A2', 'nature'),
  p('selva', 'jungle', 'jingle', 'A2', 'nature'),
  p('caverna', 'cave', 'cove', 'A2', 'nature'),
  p('penhasco', 'cliff', 'click', 'A2', 'nature'),
  p('cachoeira', 'waterfall', 'waterfull', 'A2', 'nature'),
  p('lama', 'mud', 'mad', 'A2', 'nature'),
  p('poça', 'puddle', 'paddle', 'A2', 'nature'),
  p('córrego', 'creek', 'crack', 'A2', 'nature'),
  p('bosque', 'woods', 'words', 'A2', 'nature'),
  p('pântano', 'swamp', 'swampy', 'A2', 'nature'),
  p('tempestade', 'storm', 'stork', 'A2', 'nature'),
  p('tornado', 'tornado', 'tomato', 'A2', 'nature'),
  p('furacão', 'hurricane', 'harridan', 'A2', 'nature'),
  p('terremoto', 'earthquake', 'earthquack', 'A2', 'nature'),
  p('enchente', 'flood', 'flooded', 'A2', 'nature'),
  p('seca', 'drought', 'draft', 'A2', 'nature'),
  p('primavera', 'spring', 'string', 'A2', 'nature'),
  p('verão', 'summer', 'hummer', 'A2', 'nature'),
  p('outono', 'autumn', 'authentic', 'A2', 'nature'),
  p('inverno', 'winter', 'winner', 'A2', 'nature'),
  p('manhã', 'morning', 'mourning', 'A2', 'nature'),
  p('tarde', 'afternoon', 'aftershoot', 'A2', 'nature'),
  p('noite', 'night', 'knight', 'A2', 'nature'),
  p('madrugada', 'dawn', 'down', 'B1', 'nature'),
  p('pôr-do-sol', 'sunset', 'sunser', 'B1', 'nature'),
  p('nascer-do-sol', 'sunrise', 'suntrise', 'B1', 'nature'),
  p('calor', 'heat', 'hat', 'B1', 'nature'),
  p('frio', 'cold', 'cord', 'B1', 'nature'),
  p('quente', 'hot', 'hit', 'B1', 'nature'),
  p('morno', 'warm', 'worm', 'B1', 'nature'),
  p('fresco', 'cool', 'coal', 'B1', 'nature'),
  p('úmido', 'humid', 'homid', 'B1', 'nature'),
  p('seco', 'dry', 'die', 'B1', 'nature'),
  p('ensolarado', 'sunny', 'sonny', 'B1', 'nature'),
  p('nublado', 'cloudy', 'clouded', 'B1', 'nature'),
  p('ventoso', 'windy', 'Wendy', 'B1', 'nature'),
  p('chuvoso', 'rainy', 'brainy', 'B1', 'nature'),
  p('nevado', 'snowy', 'showy', 'B1', 'nature'),
  p('geada', 'frost', 'first', 'B1', 'nature'),
  p('orvalho', 'dew', 'due', 'B1', 'nature'),
  p('aurora', 'aurora', 'oracle', 'B1', 'nature'),
  p('eclipse', 'eclipse', 'ellipse', 'B2', 'nature'),
  p('galáxia', 'galaxy', 'gallop', 'B2', 'nature'),
  p('planeta', 'planet', 'plant', 'B2', 'nature'),
  p('meteoro', 'meteor', 'motor', 'B2', 'nature'),
  p('cometa', 'comet', 'comment', 'B2', 'nature'),
  p('atmosfera', 'atmosphere', 'hemisphere', 'B2', 'nature'),
  p('habitat', 'habitat', 'habited', 'B2', 'nature'),
  p('ecossistema', 'ecosystem', 'exosystem', 'B2', 'nature'),
  p('recife', 'reef', 'roof', 'B2', 'nature'),
  p('savana', 'savanna', 'saving', 'B2', 'nature'),
  p('tundra', 'tundra', 'tundral', 'B2', 'nature'),
  p('maré', 'tide', 'tied', 'B2', 'nature'),
  p('eco', 'echo', 'etch', 'B2', 'nature'),
  p('avalanche', 'avalanche', 'advance', 'B2', 'nature'),
  p('erupção', 'eruption', 'disruption', 'B2', 'nature'),
  p('clima', 'climate', 'climax', 'B2', 'nature'),
  p('estação', 'season', 'seasoned', 'B2', 'nature'),
  p('paisagem', 'landscape', 'landsake', 'B2', 'nature'),
  p('horizonte', 'horizon', 'horizontal', 'B2', 'nature'),
  p('penumbra', 'twilight', 'twinlight', 'B2', 'nature'),
  p('arbusto', 'shrub', 'shrug', 'B2', 'nature'),
];

// ========================================================================
// BLOCO 6 — 🏃 Verbs (100 pares)
// ========================================================================
const verbs: VocabPair[] = [
  p('ser/estar', 'be', 'bee', 'A1', 'verbs'),
  p('ter', 'have', 'halve', 'A1', 'verbs'),
  p('fazer', 'do', 'doe', 'A1', 'verbs'),
  p('ir', 'go', 'gone', 'A1', 'verbs'),
  p('vir', 'come', 'calm', 'A1', 'verbs'),
  p('ver', 'see', 'sea', 'A1', 'verbs'),
  p('ouvir', 'hear', 'here', 'A1', 'verbs'),
  p('falar', 'speak', 'sneak', 'A1', 'verbs'),
  p('dizer', 'say', 'says', 'A1', 'verbs'),
  p('comer', 'eat', 'eight', 'A1', 'verbs'),
  p('beber', 'drink', 'bring', 'A1', 'verbs'),
  p('dormir', 'sleep', 'sweep', 'A1', 'verbs'),
  p('acordar', 'wake', 'walk', 'A1', 'verbs'),
  p('andar', 'walk', 'wok', 'A1', 'verbs'),
  p('correr', 'run', 'ruin', 'A1', 'verbs'),
  p('pular', 'jump', 'plumb', 'A1', 'verbs'),
  p('nadar', 'swim', 'seem', 'A1', 'verbs'),
  p('voar', 'fly', 'fry', 'A1', 'verbs'),
  p('cair', 'fall', 'full', 'A1', 'verbs'),
  p('subir', 'climb', 'chime', 'A1', 'verbs'),
  p('descer', 'descend', 'decent', 'A1', 'verbs'),
  p('abrir', 'open', 'often', 'A1', 'verbs'),
  p('fechar', 'close', 'clothes', 'A1', 'verbs'),
  p('entrar', 'enter', 'inter', 'A1', 'verbs'),
  p('sair', 'leave', 'level', 'A1', 'verbs'),
  p('sentar', 'sit', 'set', 'A1', 'verbs'),
  p('levantar', 'stand', 'stained', 'A1', 'verbs'),
  p('parar', 'stop', 'step', 'A1', 'verbs'),
  p('começar', 'start', 'stark', 'A1', 'verbs'),
  p('terminar', 'finish', 'finisher', 'A1', 'verbs'),
  p('ler', 'read', 'red', 'A2', 'verbs'),
  p('escrever', 'write', 'right', 'A2', 'verbs'),
  p('desenhar', 'draw', 'drew', 'A2', 'verbs'),
  p('pintar', 'paint', 'point', 'A2', 'verbs'),
  p('cantar', 'sing', 'sling', 'A2', 'verbs'),
  p('dançar', 'dance', 'dense', 'A2', 'verbs'),
  p('tocar', 'play', 'pray', 'A2', 'verbs'),
  p('cozinhar', 'cook', 'cork', 'A2', 'verbs'),
  p('comprar', 'buy', 'bye', 'A2', 'verbs'),
  p('vender', 'sell', 'sail', 'A2', 'verbs'),
  p('pagar', 'pay', 'pry', 'A2', 'verbs'),
  p('gastar', 'spend', 'spent', 'A2', 'verbs'),
  p('emprestar', 'lend', 'lent', 'A2', 'verbs'),
  p('pegar', 'take', 'talk', 'A2', 'verbs'),
  p('trazer', 'bring', 'brink', 'A2', 'verbs'),
  p('dar', 'give', 'gave', 'A2', 'verbs'),
  p('receber', 'receive', 'retrieve', 'A2', 'verbs'),
  p('enviar', 'send', 'sand', 'A2', 'verbs'),
  p('jogar', 'throw', 'threw', 'A2', 'verbs'),
  p('pegar-no-ar', 'catch', 'coach', 'A2', 'verbs'),
  p('empurrar', 'push', 'bush', 'A2', 'verbs'),
  p('puxar', 'pull', 'pool', 'A2', 'verbs'),
  p('cortar', 'cut', 'cud', 'A2', 'verbs'),
  p('quebrar', 'break', 'brick', 'A2', 'verbs'),
  p('consertar', 'fix', 'fox', 'A2', 'verbs'),
  p('construir', 'build', 'bold', 'A2', 'verbs'),
  p('destruir', 'destroy', 'disarray', 'A2', 'verbs'),
  p('lavar', 'wash', 'wish', 'A2', 'verbs'),
  p('limpar', 'clean', 'clan', 'A2', 'verbs'),
  p('vestir', 'wear', 'were', 'A2', 'verbs'),
  p('abraçar', 'hug', 'hog', 'B1', 'verbs'),
  p('beijar', 'kiss', 'kisses', 'B1', 'verbs'),
  p('amar', 'love', 'live', 'B1', 'verbs'),
  p('odiar', 'hate', 'hat', 'B1', 'verbs'),
  p('gostar', 'like', 'lick', 'B1', 'verbs'),
  p('querer', 'want', 'went', 'B1', 'verbs'),
  p('precisar', 'need', 'knead', 'B1', 'verbs'),
  p('pensar', 'think', 'thunk', 'B1', 'verbs'),
  p('saber', 'know', 'knew', 'B1', 'verbs'),
  p('entender', 'understand', 'understood', 'B1', 'verbs'),
  p('lembrar', 'remember', 'remindered', 'B1', 'verbs'),
  p('esquecer', 'forget', 'forgot', 'B1', 'verbs'),
  p('tentar', 'try', 'tree', 'B1', 'verbs'),
  p('conseguir', 'achieve', 'achieved', 'B1', 'verbs'),
  p('perder', 'lose', 'loose', 'B1', 'verbs'),
  p('ganhar', 'win', 'when', 'B1', 'verbs'),
  p('viajar', 'travel', 'treble', 'B1', 'verbs'),
  p('voltar', 'return', 'reburn', 'B1', 'verbs'),
  p('esperar', 'wait', 'weight', 'B1', 'verbs'),
  p('ajudar', 'help', 'hulp', 'B1', 'verbs'),
  p('aprender', 'learn', 'earn', 'B1', 'verbs'),
  p('ensinar', 'teach', 'reach', 'B1', 'verbs'),
  p('estudar', 'study', 'sturdy', 'B1', 'verbs'),
  p('trabalhar', 'work', 'walk', 'B1', 'verbs'),
  p('descansar', 'rest', 'roast', 'B1', 'verbs'),
  p('rir', 'laugh', 'leaf', 'B1', 'verbs'),
  p('chorar', 'cry', 'pry', 'B1', 'verbs'),
  p('sorrir', 'smile', 'smell', 'B1', 'verbs'),
  p('reclamar', 'complain', 'explain', 'B2', 'verbs'),
  p('concordar', 'agree', 'degree', 'B2', 'verbs'),
  p('convidar', 'invite', 'invited', 'B2', 'verbs'),
  p('aceitar', 'accept', 'except', 'B2', 'verbs'),
  p('recusar', 'refuse', 'refuge', 'B2', 'verbs'),
  p('mentir', 'lie', 'low', 'B2', 'verbs'),
  p('adivinhar', 'guess', 'guest', 'B2', 'verbs'),
  p('confiar', 'trust', 'truss', 'B2', 'verbs'),
  p('acreditar', 'believe', 'be-leaf', 'B2', 'verbs'),
  p('duvidar', 'doubt', 'debt', 'B2', 'verbs'),
  p('sonhar', 'dream', 'dread', 'B2', 'verbs'),
  p('imaginar', 'imagine', 'imaging', 'B2', 'verbs'),
  p('decidir', 'decide', 'deride', 'B2', 'verbs'),
  p('escolher', 'choose', 'chose', 'B2', 'verbs'),
  p('descobrir', 'discover', 'uncover', 'B2', 'verbs'),
];

// ========================================================================
// BLOCO 7 — 💼 Work & School (100 pares)
// ========================================================================
const workSchool: VocabPair[] = [
  p('escola', 'school', 'scold', 'A1', 'work-school'),
  p('aluno', 'student', 'stewart', 'A1', 'work-school'),
  p('professor', 'teacher', 'preacher', 'A1', 'work-school'),
  p('aula', 'class', 'clash', 'A1', 'work-school'),
  p('lição', 'lesson', 'listen', 'A1', 'work-school'),
  p('prova', 'test', 'text', 'A1', 'work-school'),
  p('nota', 'grade', 'grate', 'A1', 'work-school'),
  p('dever', 'homework', 'homemark', 'A1', 'work-school'),
  p('quadro', 'board', 'bored', 'A1', 'work-school'),
  p('giz', 'chalk', 'chock', 'A1', 'work-school'),
  p('mesa-escolar', 'desk', 'disk', 'A1', 'work-school'),
  p('mochila-escolar', 'schoolbag', 'scoldbag', 'A1', 'work-school'),
  p('colega', 'classmate', 'classmute', 'A1', 'work-school'),
  p('diretor', 'principal', 'principle', 'A1', 'work-school'),
  p('biblioteca', 'library', 'livery', 'A1', 'work-school'),
  p('cantina', 'cafeteria', 'cemetery', 'A1', 'work-school'),
  p('recreio', 'recess', 'recipe', 'A1', 'work-school'),
  p('matemática', 'math', 'moth', 'A1', 'work-school'),
  p('ciência', 'science', 'silence', 'A1', 'work-school'),
  p('história', 'history', 'mystery', 'A1', 'work-school'),
  p('geografia', 'geography', 'jog-graphy', 'A1', 'work-school'),
  p('arte', 'art', 'art-y', 'A1', 'work-school'),
  p('música', 'music', 'mystic', 'A1', 'work-school'),
  p('educação-física', 'gym-class', 'gem-class', 'A1', 'work-school'),
  p('caderno-escolar', 'workbook', 'wardbook', 'A1', 'work-school'),
  p('mochila', 'backpack', 'backtrack', 'A1', 'work-school'),
  p('apontador', 'sharpener', 'sharper', 'A1', 'work-school'),
  p('borracha-escolar', 'eraser', 'eraze', 'A1', 'work-school'),
  p('marca-texto', 'highlighter', 'highlighted', 'A1', 'work-school'),
  p('calculadora', 'calculator', 'calibrator', 'A1', 'work-school'),
  p('trabalho', 'work', 'worth', 'A2', 'work-school'),
  p('emprego', 'job', 'jog', 'A2', 'work-school'),
  p('escritório', 'office', 'offer', 'A2', 'work-school'),
  p('chefe', 'boss', 'boast', 'A2', 'work-school'),
  p('funcionário', 'employee', 'employ-eat', 'A2', 'work-school'),
  p('empresa', 'company', 'comply', 'A2', 'work-school'),
  p('sócio', 'partner', 'pardon', 'A2', 'work-school'),
  p('colega-de-trabalho', 'coworker', 'coward', 'A2', 'work-school'),
  p('cliente', 'client', 'quiet', 'A2', 'work-school'),
  p('reunião', 'meeting', 'melting', 'A2', 'work-school'),
  p('apresentação', 'presentation', 'presentmen', 'A2', 'work-school'),
  p('relatório', 'report', 'retort', 'A2', 'work-school'),
  p('projeto', 'project', 'protect', 'A2', 'work-school'),
  p('tarefa', 'task', 'tusk', 'A2', 'work-school'),
  p('prazo', 'deadline', 'deadlift', 'A2', 'work-school'),
  p('salário', 'salary', 'celery', 'A2', 'work-school'),
  p('promoção', 'promotion', 'motion', 'A2', 'work-school'),
  p('contrato', 'contract', 'contact', 'A2', 'work-school'),
  p('currículo', 'resume', 'rescue', 'A2', 'work-school'),
  p('entrevista', 'interview', 'interjive', 'A2', 'work-school'),
  p('carreira', 'career', 'courier', 'A2', 'work-school'),
  p('demitir', 'fire', 'fry', 'A2', 'work-school'),
  p('contratar', 'hire', 'higher', 'A2', 'work-school'),
  p('renunciar', 'quit', 'quite', 'A2', 'work-school'),
  p('aposentar', 'retire', 'retire-d', 'A2', 'work-school'),
  p('hora-extra', 'overtime', 'overshine', 'A2', 'work-school'),
  p('folga', 'day-off', 'day-of', 'A2', 'work-school'),
  p('férias', 'vacation', 'vocation', 'A2', 'work-school'),
  p('viagem-de-trabalho', 'business-trip', 'business-trap', 'A2', 'work-school'),
  p('horário', 'schedule', 'scheddle', 'A2', 'work-school'),
  p('agenda', 'agenda', 'again-da', 'A2', 'work-school'),
  p('lembrete', 'reminder', 'remainder', 'B1', 'work-school'),
  p('e-mail', 'email', 'enamel', 'B1', 'work-school'),
  p('mensagem', 'message', 'massage', 'B1', 'work-school'),
  p('reunião-virtual', 'video-call', 'video-call-y', 'B1', 'work-school'),
  p('arquivo', 'file', 'fill', 'B1', 'work-school'),
  p('pasta', 'folder', 'fodder', 'B1', 'work-school'),
  p('documento', 'document', 'docudrama', 'B1', 'work-school'),
  p('senha', 'password', 'pastword', 'B1', 'work-school'),
  p('login', 'login', 'lagoon', 'B1', 'work-school'),
  p('balança', 'budget', 'bucket', 'B1', 'work-school'),
  p('imposto', 'tax', 'tack', 'B1', 'work-school'),
  p('fatura', 'invoice', 'involve', 'B1', 'work-school'),
  p('nota-fiscal', 'receipt', 'recipe', 'B1', 'work-school'),
  p('assinatura', 'signature', 'signal-nature', 'B1', 'work-school'),
  p('reembolso', 'refund', 'reflux', 'B1', 'work-school'),
  p('comissão', 'commission', 'commotion', 'B1', 'work-school'),
  p('bônus', 'bonus', 'bonos', 'B1', 'work-school'),
  p('benefício', 'benefit', 'benefice', 'B1', 'work-school'),
  p('plano-de-saúde', 'health-plan', 'wealth-plan', 'B1', 'work-school'),
  p('pensão', 'pension', 'tension', 'B1', 'work-school'),
  p('universidade', 'university', 'unity', 'B2', 'work-school'),
  p('faculdade', 'college', 'collage', 'B2', 'work-school'),
  p('curso', 'course', 'coarse', 'B2', 'work-school'),
  p('palestra', 'lecture', 'lector', 'B2', 'work-school'),
  p('seminário', 'seminar', 'seam-in-ar', 'B2', 'work-school'),
  p('mestrado', 'masters', 'mutters', 'B2', 'work-school'),
  p('doutorado', 'PhD', 'PhD-y', 'B2', 'work-school'),
  p('bolsa-de-estudos', 'scholarship', 'schoolarship', 'B2', 'work-school'),
  p('diploma', 'degree', 'decree', 'B2', 'work-school'),
  p('formatura', 'graduation', 'radiation', 'B2', 'work-school'),
  p('pesquisa', 'research', 'reach', 'B2', 'work-school'),
  p('tese', 'thesis', 'theses', 'B2', 'work-school'),
  p('citação', 'citation', 'sitation', 'B2', 'work-school'),
  p('plágio', 'plagiarism', 'prairie-ism', 'B2', 'work-school'),
  p('bibliografia', 'bibliography', 'biography', 'B2', 'work-school'),
  p('estágio', 'internship', 'intern-shit', 'B2', 'work-school'),
  p('tutor', 'tutor', 'tudor', 'B2', 'work-school'),
  p('mentor', 'mentor', 'tenor', 'B2', 'work-school'),
  p('sindicato', 'union', 'onion', 'B2', 'work-school'),
  p('greve', 'strike', 'strict', 'B2', 'work-school'),
];

// ========================================================================
// BLOCO 8 — 🚗 Transport & City (100 pares)
// ========================================================================
const transportCity: VocabPair[] = [
  p('carro', 'car', 'care', 'A1', 'transport-city'),
  p('ônibus', 'bus', 'buss', 'A1', 'transport-city'),
  p('bicicleta', 'bicycle', 'beside', 'A1', 'transport-city'),
  p('moto', 'motorcycle', 'motorcar', 'A1', 'transport-city'),
  p('trem', 'train', 'brain', 'A1', 'transport-city'),
  p('avião', 'plane', 'plain', 'A1', 'transport-city'),
  p('navio', 'ship', 'sheep', 'A1', 'transport-city'),
  p('barco', 'boat', 'bought', 'A1', 'transport-city'),
  p('caminhão', 'truck', 'trick', 'A1', 'transport-city'),
  p('táxi', 'taxi', 'tax-y', 'A1', 'transport-city'),
  p('metrô', 'subway', 'subdue', 'A1', 'transport-city'),
  p('rua', 'street', 'strait', 'A1', 'transport-city'),
  p('avenida', 'avenue', 'advent', 'A1', 'transport-city'),
  p('estrada', 'road', 'road-y', 'A1', 'transport-city'),
  p('rodovia', 'highway', 'halfway', 'A1', 'transport-city'),
  p('esquina', 'corner', 'coroner', 'A1', 'transport-city'),
  p('cruzamento', 'crossroad', 'grossroad', 'A1', 'transport-city'),
  p('semáforo', 'traffic-light', 'traffic-lite', 'A1', 'transport-city'),
  p('calçada', 'sidewalk', 'slidewalk', 'A1', 'transport-city'),
  p('ciclovia', 'bike-lane', 'bike-lame', 'A1', 'transport-city'),
  p('ponte', 'bridge', 'fridge', 'A1', 'transport-city'),
  p('túnel', 'tunnel', 'tonnel', 'A1', 'transport-city'),
  p('aeroporto', 'airport', 'apart', 'A1', 'transport-city'),
  p('rodoviária', 'bus-station', 'best-station', 'A1', 'transport-city'),
  p('porto', 'port', 'pore', 'A1', 'transport-city'),
  p('estação', 'station', 'starting', 'A1', 'transport-city'),
  p('mapa', 'map', 'mop', 'A1', 'transport-city'),
  p('placa', 'sign', 'sigh', 'A1', 'transport-city'),
  p('trânsito', 'traffic', 'terrific', 'A1', 'transport-city'),
  p('volante', 'wheel', 'whale', 'A1', 'transport-city'),
  p('pneu', 'tire', 'tier', 'A2', 'transport-city'),
  p('farol', 'headlight', 'headlike', 'A2', 'transport-city'),
  p('para-brisa', 'windshield', 'windcheeld', 'A2', 'transport-city'),
  p('motor', 'engine', 'enjoin', 'A2', 'transport-city'),
  p('freio', 'brake', 'break', 'A2', 'transport-city'),
  p('acelerador', 'accelerator', 'alligator', 'A2', 'transport-city'),
  p('combustível', 'fuel', 'fool', 'A2', 'transport-city'),
  p('posto-de-gasolina', 'gas-station', 'gaz-station', 'A2', 'transport-city'),
  p('estacionamento', 'parking-lot', 'parquing-lot', 'A2', 'transport-city'),
  p('garagem', 'garage', 'garish', 'A2', 'transport-city'),
  p('cinto-de-segurança', 'seatbelt', 'seedbelt', 'A2', 'transport-city'),
  p('carona', 'ride', 'rode', 'A2', 'transport-city'),
  p('passageiro', 'passenger', 'presenter', 'A2', 'transport-city'),
  p('motorista', 'driver', 'driven', 'A2', 'transport-city'),
  p('pedestre', 'pedestrian', 'pedestal-an', 'A2', 'transport-city'),
  p('engarrafamento', 'traffic-jam', 'terrific-jam', 'A2', 'transport-city'),
  p('multa', 'fine', 'fin', 'A2', 'transport-city'),
  p('acidente', 'accident', 'incident', 'A2', 'transport-city'),
  p('batida', 'crash', 'cash', 'A2', 'transport-city'),
  p('passagem', 'ticket', 'tacket', 'A2', 'transport-city'),
  p('cidade', 'city', 'silly', 'A2', 'transport-city'),
  p('país', 'country', 'county', 'A2', 'transport-city'),
  p('bairro', 'neighborhood', 'naybor-hood', 'A2', 'transport-city'),
  p('centro', 'downtown', 'downtime', 'A2', 'transport-city'),
  p('subúrbio', 'suburb', 'suberm', 'A2', 'transport-city'),
  p('capital', 'capital', 'capsule', 'A2', 'transport-city'),
  p('praça', 'square', 'scare', 'A2', 'transport-city'),
  p('parque', 'park', 'pork', 'A2', 'transport-city'),
  p('mercado', 'market', 'mark-it', 'A2', 'transport-city'),
  p('loja', 'shop', 'shape', 'A2', 'transport-city'),
  p('shopping', 'mall', 'mall-y', 'A2', 'transport-city'),
  p('banco', 'bank', 'bunk', 'B1', 'transport-city'),
  p('caixa-eletrônico', 'ATM', 'A-T-N', 'B1', 'transport-city'),
  p('correio', 'post-office', 'past-office', 'B1', 'transport-city'),
  p('hospital', 'hospital', 'hostile', 'B1', 'transport-city'),
  p('farmácia', 'pharmacy', 'farming', 'B1', 'transport-city'),
  p('delegacia', 'police-station', 'polyglot-station', 'B1', 'transport-city'),
  p('quartel', 'barracks', 'barrachs', 'B1', 'transport-city'),
  p('bombeiros', 'fire-station', 'fire-stain', 'B1', 'transport-city'),
  p('igreja', 'church', 'churn', 'B1', 'transport-city'),
  p('cemitério', 'cemetery', 'celery', 'B1', 'transport-city'),
  p('museu', 'museum', 'medium', 'B1', 'transport-city'),
  p('teatro', 'theater', 'theater-y', 'B1', 'transport-city'),
  p('cinema', 'cinema', 'sinema', 'B1', 'transport-city'),
  p('restaurante', 'restaurant', 'resturant', 'B1', 'transport-city'),
  p('hotel', 'hotel', 'hostel', 'B1', 'transport-city'),
  p('estádio', 'stadium', 'studio', 'B1', 'transport-city'),
  p('academia', 'gym', 'gem', 'B1', 'transport-city'),
  p('padaria', 'bakery', 'bickery', 'B1', 'transport-city'),
  p('açougue', 'butcher-shop', 'butcher-shot', 'B1', 'transport-city'),
  p('salão', 'salon', 'saloon', 'B1', 'transport-city'),
  p('lavanderia', 'laundromat', 'laundryfolk', 'B2', 'transport-city'),
  p('embaixada', 'embassy', 'embarrass', 'B2', 'transport-city'),
  p('prefeitura', 'city-hall', 'city-wall', 'B2', 'transport-city'),
  p('tribunal', 'courthouse', 'courtouse', 'B2', 'transport-city'),
  p('catedral', 'cathedral', 'cashedral', 'B2', 'transport-city'),
  p('arranha-céu', 'skyscraper', 'slyscraper', 'B2', 'transport-city'),
  p('orla', 'waterfront', 'watersport', 'B2', 'transport-city'),
  p('marina', 'marina', 'mariner', 'B2', 'transport-city'),
  p('ferrovia', 'railway', 'railray', 'B2', 'transport-city'),
  p('cruzeiro', 'cruise', 'crucy', 'B2', 'transport-city'),
  p('balsa', 'ferry', 'fiery', 'B2', 'transport-city'),
  p('helicóptero', 'helicopter', 'heliport-er', 'B2', 'transport-city'),
  p('carga', 'cargo', 'carga', 'B2', 'transport-city'),
  p('despacho', 'shipment', 'ship-mint', 'B2', 'transport-city'),
  p('entrega', 'delivery', 'deliver-a', 'B2', 'transport-city'),
  p('rota', 'route', 'rut', 'B2', 'transport-city'),
  p('desvio', 'detour', 'dehor', 'B2', 'transport-city'),
  p('atalho', 'shortcut', 'shortcute', 'B2', 'transport-city'),
  p('rotatória', 'roundabout', 'around-about', 'B2', 'transport-city'),
  p('calçadão', 'promenade', 'promensade', 'B2', 'transport-city'),
];

// ========================================================================
// BLOCO 9 — 🕒 Time & Numbers (100 pares)
// ========================================================================
const timeNumbers: VocabPair[] = [
  p('um', 'one', 'own', 'A1', 'time-numbers'),
  p('dois', 'two', 'too', 'A1', 'time-numbers'),
  p('três', 'three', 'tree', 'A1', 'time-numbers'),
  p('quatro', 'four', 'for', 'A1', 'time-numbers'),
  p('cinco', 'five', 'hive', 'A1', 'time-numbers'),
  p('seis', 'six', 'sick', 'A1', 'time-numbers'),
  p('sete', 'seven', 'sevens', 'A1', 'time-numbers'),
  p('oito', 'eight', 'ate', 'A1', 'time-numbers'),
  p('nove', 'nine', 'mine', 'A1', 'time-numbers'),
  p('dez', 'ten', 'tin', 'A1', 'time-numbers'),
  p('onze', 'eleven', 'elephant', 'A1', 'time-numbers'),
  p('doze', 'twelve', 'twelf', 'A1', 'time-numbers'),
  p('treze', 'thirteen', 'thumbteen', 'A1', 'time-numbers'),
  p('catorze', 'fourteen', 'fourten', 'A1', 'time-numbers'),
  p('quinze', 'fifteen', 'finteen', 'A1', 'time-numbers'),
  p('dezesseis', 'sixteen', 'sickteen', 'A1', 'time-numbers'),
  p('dezessete', 'seventeen', 'seveteen', 'A1', 'time-numbers'),
  p('dezoito', 'eighteen', 'ateteen', 'A1', 'time-numbers'),
  p('dezenove', 'nineteen', 'minenteen', 'A1', 'time-numbers'),
  p('vinte', 'twenty', 'twisty', 'A1', 'time-numbers'),
  p('trinta', 'thirty', 'dirty', 'A1', 'time-numbers'),
  p('quarenta', 'forty', 'fourty', 'A1', 'time-numbers'),
  p('cinquenta', 'fifty', 'fifty-y', 'A1', 'time-numbers'),
  p('sessenta', 'sixty', 'sicky', 'A1', 'time-numbers'),
  p('setenta', 'seventy', 'seventh', 'A1', 'time-numbers'),
  p('oitenta', 'eighty', 'eighty-y', 'A1', 'time-numbers'),
  p('noventa', 'ninety', 'nightly', 'A1', 'time-numbers'),
  p('cem', 'hundred', 'hundreded', 'A1', 'time-numbers'),
  p('mil', 'thousand', 'thousanded', 'A1', 'time-numbers'),
  p('milhão', 'million', 'melon', 'A1', 'time-numbers'),
  p('primeiro', 'first', 'frost', 'A2', 'time-numbers'),
  p('segundo', 'second', 'secret', 'A2', 'time-numbers'),
  p('terceiro', 'third', 'thread', 'A2', 'time-numbers'),
  p('quarto', 'fourth', 'forth', 'A2', 'time-numbers'),
  p('quinto', 'fifth', 'fist', 'A2', 'time-numbers'),
  p('metade', 'half', 'halve', 'A2', 'time-numbers'),
  p('dobro', 'double', 'doubloon', 'A2', 'time-numbers'),
  p('triplo', 'triple', 'tripe', 'A2', 'time-numbers'),
  p('quádruplo', 'quadruple', 'quadrupid', 'A2', 'time-numbers'),
  p('dúzia', 'dozen', 'doze-n', 'A2', 'time-numbers'),
  p('par', 'pair', 'pare', 'A2', 'time-numbers'),
  p('zero', 'zero', 'hero', 'A2', 'time-numbers'),
  p('segundo-tempo', 'second', 'secret', 'A2', 'time-numbers'),
  p('minuto', 'minute', 'minute-d', 'A2', 'time-numbers'),
  p('hora', 'hour', 'our', 'A2', 'time-numbers'),
  p('dia', 'day', 'dye', 'A2', 'time-numbers'),
  p('semana', 'week', 'weak', 'A2', 'time-numbers'),
  p('mês', 'month', 'mouth', 'A2', 'time-numbers'),
  p('ano', 'year', 'ear', 'A2', 'time-numbers'),
  p('década', 'decade', 'decoy', 'A2', 'time-numbers'),
  p('século', 'century', 'cement-ary', 'A2', 'time-numbers'),
  p('milênio', 'millennium', 'minimum', 'A2', 'time-numbers'),
  p('segunda', 'Monday', 'monday-y', 'A2', 'time-numbers'),
  p('terça', 'Tuesday', 'Thursday', 'A2', 'time-numbers'),
  p('quarta', 'Wednesday', 'Wendsday', 'A2', 'time-numbers'),
  p('quinta', 'Thursday', 'Tuesday', 'A2', 'time-numbers'),
  p('sexta', 'Friday', 'fried-day', 'A2', 'time-numbers'),
  p('sábado', 'Saturday', 'Sutherland', 'A2', 'time-numbers'),
  p('domingo', 'Sunday', 'Soonday', 'A2', 'time-numbers'),
  p('janeiro', 'January', 'Junior', 'A2', 'time-numbers'),
  p('fevereiro', 'February', 'Februality', 'B1', 'time-numbers'),
  p('março', 'March', 'Match', 'B1', 'time-numbers'),
  p('abril', 'April', 'Aprill', 'B1', 'time-numbers'),
  p('maio', 'May', 'Mai', 'B1', 'time-numbers'),
  p('junho', 'June', 'Jude', 'B1', 'time-numbers'),
  p('julho', 'July', 'Jewly', 'B1', 'time-numbers'),
  p('agosto', 'August', 'Augast', 'B1', 'time-numbers'),
  p('setembro', 'September', 'Semptembra', 'B1', 'time-numbers'),
  p('outubro', 'October', 'Octubre', 'B1', 'time-numbers'),
  p('novembro', 'November', 'Novemba', 'B1', 'time-numbers'),
  p('dezembro', 'December', 'Decembre', 'B1', 'time-numbers'),
  p('ontem', 'yesterday', 'yasterday', 'B1', 'time-numbers'),
  p('hoje', 'today', 'toodai', 'B1', 'time-numbers'),
  p('amanhã', 'tomorrow', 'tommorow', 'B1', 'time-numbers'),
  p('cedo', 'early', 'earl', 'B1', 'time-numbers'),
  p('tarde-adj', 'late', 'lite', 'B1', 'time-numbers'),
  p('agora', 'now', 'know', 'B1', 'time-numbers'),
  p('depois', 'later', 'latter', 'B1', 'time-numbers'),
  p('sempre', 'always', 'allows', 'B1', 'time-numbers'),
  p('nunca', 'never', 'nerver', 'B1', 'time-numbers'),
  p('às-vezes', 'sometimes', 'somtimes', 'B1', 'time-numbers'),
  p('frequentemente', 'often', 'oven', 'B1', 'time-numbers'),
  p('raramente', 'rarely', 'really', 'B1', 'time-numbers'),
  p('diário', 'daily', 'deadly', 'B1', 'time-numbers'),
  p('semanal', 'weekly', 'weakly', 'B1', 'time-numbers'),
  p('mensal', 'monthly', 'mouthly', 'B1', 'time-numbers'),
  p('anual', 'yearly', 'early', 'B1', 'time-numbers'),
  p('quinzenal', 'biweekly', 'biwookly', 'B2', 'time-numbers'),
  p('trimestral', 'quarterly', 'quartly', 'B2', 'time-numbers'),
  p('semestral', 'biyearly', 'bi-early', 'B2', 'time-numbers'),
  p('relógio-digital', 'digital-clock', 'digit-clock', 'B2', 'time-numbers'),
  p('relógio-analógico', 'analog-clock', 'annoy-clock', 'B2', 'time-numbers'),
  p('cronômetro', 'stopwatch', 'stop-watch', 'B2', 'time-numbers'),
  p('despertador', 'alarm-clock', 'alarmed-clock', 'B2', 'time-numbers'),
  p('calendário', 'calendar', 'candolar', 'B2', 'time-numbers'),
  p('feriado', 'holiday', 'holly-day', 'B2', 'time-numbers'),
  p('aniversário', 'birthday', 'birthdaze', 'B2', 'time-numbers'),
  p('semestre', 'semester', 'seemester', 'B2', 'time-numbers'),
  p('prazo-limite', 'deadline', 'deadlift', 'B2', 'time-numbers'),
  p('período', 'period', 'periodic', 'B2', 'time-numbers'),
  p('era', 'era', 'era-l', 'B2', 'time-numbers'),
  p('fase', 'phase', 'face', 'B2', 'time-numbers'),
  p('cronograma', 'timeline', 'timelime', 'B2', 'time-numbers'),
];

// ========================================================================
// BLOCO 10 — 😊 Emotions & Social (100 pares)
// ========================================================================
const emotionsSocial: VocabPair[] = [
  p('feliz', 'happy', 'happen', 'A1', 'emotions-social'),
  p('triste', 'sad', 'said', 'A1', 'emotions-social'),
  p('bravo', 'angry', 'angle', 'A1', 'emotions-social'),
  p('com-medo', 'afraid', 'abrade', 'A1', 'emotions-social'),
  p('cansado', 'tired', 'tied', 'A1', 'emotions-social'),
  p('com-fome', 'hungry', 'hungary', 'A1', 'emotions-social'),
  p('com-sede', 'thirsty', 'thirty', 'A1', 'emotions-social'),
  p('com-sono', 'sleepy', 'slippy', 'A1', 'emotions-social'),
  p('animado', 'excited', 'excised', 'A1', 'emotions-social'),
  p('entediado', 'bored', 'board', 'A1', 'emotions-social'),
  p('confuso', 'confused', 'confessed', 'A1', 'emotions-social'),
  p('surpreso', 'surprised', 'sunrised', 'A1', 'emotions-social'),
  p('preocupado', 'worried', 'married', 'A1', 'emotions-social'),
  p('relaxado', 'relaxed', 'reflexed', 'A1', 'emotions-social'),
  p('nervoso', 'nervous', 'marvelous', 'A1', 'emotions-social'),
  p('calmo', 'calm', 'palm', 'A1', 'emotions-social'),
  p('alegre', 'cheerful', 'cherful', 'A1', 'emotions-social'),
  p('depressão', 'sadness', 'softness', 'A1', 'emotions-social'),
  p('amor', 'love', 'live', 'A1', 'emotions-social'),
  p('ódio', 'hate', 'hat', 'A1', 'emotions-social'),
  p('medo', 'fear', 'feed', 'A1', 'emotions-social'),
  p('raiva', 'anger', 'angle', 'A1', 'emotions-social'),
  p('ciúme', 'jealousy', 'jealose', 'A1', 'emotions-social'),
  p('inveja', 'envy', 'envoy', 'A1', 'emotions-social'),
  p('orgulho', 'pride', 'price', 'A1', 'emotions-social'),
  p('vergonha', 'shame', 'shane', 'A1', 'emotions-social'),
  p('culpa', 'guilt', 'gilt', 'A1', 'emotions-social'),
  p('alívio', 'relief', 'leaf', 'A1', 'emotions-social'),
  p('esperança', 'hope', 'hop', 'A1', 'emotions-social'),
  p('desespero', 'despair', 'debonair', 'A1', 'emotions-social'),
  p('amigo', 'friend', 'fiend', 'A2', 'emotions-social'),
  p('amiga', 'friend', 'fiend', 'A2', 'emotions-social'),
  p('família', 'family', 'formally', 'A2', 'emotions-social'),
  p('pai', 'father', 'farther', 'A2', 'emotions-social'),
  p('mãe', 'mother', 'mothee', 'A2', 'emotions-social'),
  p('irmão', 'brother', 'broder', 'A2', 'emotions-social'),
  p('irmã', 'sister', 'sisterly', 'A2', 'emotions-social'),
  p('filho', 'son', 'sun', 'A2', 'emotions-social'),
  p('filha', 'daughter', 'doughter', 'A2', 'emotions-social'),
  p('avô', 'grandfather', 'grandfather-y', 'A2', 'emotions-social'),
  p('avó', 'grandmother', 'grandmonther', 'A2', 'emotions-social'),
  p('neto', 'grandson', 'grandsun', 'A2', 'emotions-social'),
  p('neta', 'granddaughter', 'grand-doughter', 'A2', 'emotions-social'),
  p('tio', 'uncle', 'ankle', 'A2', 'emotions-social'),
  p('tia', 'aunt', 'ant', 'A2', 'emotions-social'),
  p('primo', 'cousin', 'cousine', 'A2', 'emotions-social'),
  p('sobrinho', 'nephew', 'nephewer', 'A2', 'emotions-social'),
  p('sobrinha', 'niece', 'nice', 'A2', 'emotions-social'),
  p('padrasto', 'stepfather', 'stepfarther', 'A2', 'emotions-social'),
  p('madrasta', 'stepmother', 'stopmother', 'A2', 'emotions-social'),
  p('noivo', 'fiancé', 'finance', 'A2', 'emotions-social'),
  p('noiva', 'fiancée', 'financee', 'A2', 'emotions-social'),
  p('marido', 'husband', 'husbandry', 'A2', 'emotions-social'),
  p('esposa', 'wife', 'life', 'A2', 'emotions-social'),
  p('namorado', 'boyfriend', 'bayfriend', 'A2', 'emotions-social'),
  p('namorada', 'girlfriend', 'girlfiend', 'A2', 'emotions-social'),
  p('vizinho', 'neighbor', 'naighbor', 'A2', 'emotions-social'),
  p('conhecido', 'acquaintance', 'acquaintance-r', 'A2', 'emotions-social'),
  p('estranho', 'stranger', 'strongest', 'A2', 'emotions-social'),
  p('convidado', 'guest', 'gust', 'A2', 'emotions-social'),
  p('anfitrião', 'host', 'post', 'B1', 'emotions-social'),
  p('encontro', 'date', 'data', 'B1', 'emotions-social'),
  p('casamento', 'wedding', 'welding', 'B1', 'emotions-social'),
  p('divórcio', 'divorce', 'divorse', 'B1', 'emotions-social'),
  p('separação', 'separation', 'seperation', 'B1', 'emotions-social'),
  p('reconciliação', 'reconciliation', 'recolinization', 'B1', 'emotions-social'),
  p('amizade', 'friendship', 'fiendship', 'B1', 'emotions-social'),
  p('relacionamento', 'relationship', 'relation-ship', 'B1', 'emotions-social'),
  p('confiança', 'trust', 'truss', 'B1', 'emotions-social'),
  p('lealdade', 'loyalty', 'royalty', 'B1', 'emotions-social'),
  p('honestidade', 'honesty', 'hostesy', 'B1', 'emotions-social'),
  p('respeito', 'respect', 'receipt', 'B1', 'emotions-social'),
  p('gentileza', 'kindness', 'kingdom', 'B1', 'emotions-social'),
  p('generosidade', 'generosity', 'gene-osity', 'B1', 'emotions-social'),
  p('solidariedade', 'solidarity', 'solid-city', 'B1', 'emotions-social'),
  p('simpatia', 'sympathy', 'symphony', 'B1', 'emotions-social'),
  p('empatia', 'empathy', 'empty', 'B1', 'emotions-social'),
  p('compaixão', 'compassion', 'concession', 'B1', 'emotions-social'),
  p('tolerância', 'tolerance', 'torrents', 'B1', 'emotions-social'),
  p('paciência', 'patience', 'patients', 'B1', 'emotions-social'),
  p('gratidão', 'gratitude', 'gravitate', 'B1', 'emotions-social'),
  p('desconfiança', 'suspicion', 'sustention', 'B2', 'emotions-social'),
  p('traição', 'betrayal', 'betrayed', 'B2', 'emotions-social'),
  p('deslealdade', 'disloyalty', 'disload', 'B2', 'emotions-social'),
  p('arrogância', 'arrogance', 'aggregance', 'B2', 'emotions-social'),
  p('humildade', 'humility', 'humidity', 'B2', 'emotions-social'),
  p('timidez', 'shyness', 'shineness', 'B2', 'emotions-social'),
  p('extroversão', 'extroversion', 'extra-vision', 'B2', 'emotions-social'),
  p('introspecção', 'introspection', 'interception', 'B2', 'emotions-social'),
  p('carinho', 'affection', 'affection-al', 'B2', 'emotions-social'),
  p('ternura', 'tenderness', 'tenderless', 'B2', 'emotions-social'),
  p('saudade', 'longing', 'lunging', 'B2', 'emotions-social'),
  p('nostalgia', 'nostalgia', 'nostratis', 'B2', 'emotions-social'),
  p('melancolia', 'melancholy', 'melanholy', 'B2', 'emotions-social'),
  p('eufória', 'euphoria', 'euphorium', 'B2', 'emotions-social'),
  p('pânico', 'panic', 'panicked', 'B2', 'emotions-social'),
  p('fúria', 'fury', 'furry', 'B2', 'emotions-social'),
  p('resignação', 'resignation', 'resignatton', 'B2', 'emotions-social'),
  p('indignação', 'indignation', 'indigenation', 'B2', 'emotions-social'),
  p('remorso', 'remorse', 'remote', 'B2', 'emotions-social'),
  p('vingança', 'revenge', 'revenge-d', 'B2', 'emotions-social'),
];

// ========================================================================
// Export — pool unificado
// ========================================================================

/** Pool unificado. 1000 pares, ordem preservada por tópico + nível. */
export const VOCAB_POOL: VocabPair[] = [
  ...animals,
  ...food,
  ...colorsBody,
  ...house,
  ...nature,
  ...verbs,
  ...workSchool,
  ...transportCity,
  ...timeNumbers,
  ...emotionsSocial,
];

/** Conta por nível — útil pra testes de integridade. */
export const VOCAB_COUNT_BY_LEVEL: Record<VocabLevel, number> = VOCAB_POOL.reduce(
  (acc, pair) => {
    acc[pair.level] = (acc[pair.level] ?? 0) + 1;
    return acc;
  },
  { A1: 0, A2: 0, B1: 0, B2: 0 } as Record<VocabLevel, number>,
);

/** Total geral — tem que ser ≥ 1000 conforme spec. */
export const VOCAB_TOTAL = VOCAB_POOL.length;

/**
 * Adapter pra código legado que espera FlappyPair { promptPt, correct, wrong }.
 * Games novos devem usar `VOCAB_POOL` direto.
 */
export function toFlappyPair(v: VocabPair): FlappyPair {
  return { promptPt: v.pt, correct: v.en, wrong: v.wrong };
}

/** Pool no formato legado, já adaptado. */
export const LEGACY_POOL: FlappyPair[] = VOCAB_POOL.map(toFlappyPair);

/**
 * Embaralha dentro de blocos de `blockSize` preservando a curva de
 * dificuldade: cada bloco fica shuffled mas blocos anteriores (mais fáceis)
 * vêm primeiro.
 */
export function blockShuffle<T>(arr: T[], blockSize = 50): T[] {
  const out: T[] = [];
  for (let i = 0; i < arr.length; i += blockSize) {
    const block = arr.slice(i, i + blockSize);
    for (let j = block.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      const tmp = block[j] as T;
      block[j] = block[k] as T;
      block[k] = tmp;
    }
    out.push(...block);
  }
  return out;
}

/**
 * Amostra N pares aleatórios do pool legado (formato FlappyPair). Útil pra
 * minigames em round único (WordBuilder, Hangman, WordMatch por rodada).
 * `bias` controla a dificuldade: 0 = todo o pool uniforme, 1 = só final
 * (mais difícil). Por padrão amostra uniforme.
 */
export function samplePairs(n: number, bias = 0): FlappyPair[] {
  const pool = LEGACY_POOL;
  const start = Math.floor(pool.length * bias);
  const slice = pool.slice(start);
  const out: FlappyPair[] = [];
  const used = new Set<number>();
  while (out.length < n && used.size < slice.length) {
    const idx = Math.floor(Math.random() * slice.length);
    if (used.has(idx)) continue;
    used.add(idx);
    out.push(slice[idx]!);
  }
  return out;
}

/**
 * Amostra progressiva pra minigames com rounds: `round` começa em 1, e o
 * bias sobe lentamente (round 1 = pool inicial, rounds finais = final do
 * pool). Cap em bias 0.7 pra não ficar só B2.
 */
export function samplePairsForRound(n: number, round: number, maxRound = 10): FlappyPair[] {
  const bias = Math.min(0.7, ((round - 1) / Math.max(1, maxRound)) * 0.8);
  return samplePairs(n, bias);
}
